import {
  Alert,
  AlertSection,
  AlertType,
  AloftPrice,
  CompType,
  isDefined,
  isUndefined,
  schAloftArea,
} from '@aloft/toolkit-shared';

import {
  type MutableRefObject,
  type ReactNode,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';

import {
  getVacantLandChartData,
  getVacantLandSalesExponentialFit,
} from '../../../../components/graphs/costApproach/utils.ts';
import { useVacantLandSales } from '../../../../hooks/useVacantLandSales.tsx';
import { selectCompAdjustments } from '../../../../redux/features/selectors/adjustmentSelectors.ts';
import { selectAlerts } from '../../../../redux/features/selectors/commentarySelectors/selectAlerts.ts';
import {
  selectCompSequence,
  selectCompTypes,
  selectIncludeCostApproach,
  selectOrder,
} from '../../../../redux/features/selectors/orderSelectors.ts';
import { selectPriorSales } from '../../../../redux/features/selectors/priorSalesSelectors.ts';
import { selectEditablePropertyDataById } from '../../../../redux/features/selectors/propertyDataSelectors/editablePropertyDataSelectors.ts';
import { selectImprovementEntityIds } from '../../../../redux/features/selectors/propertyDataSelectors/improvementsSelectors.ts';
import { selectPropertyDataEntities } from '../../../../redux/features/selectors/propertyDataSelectors/propertyDataSelectors.ts';
import { selectImprovementsCosts } from '../../../../redux/features/selectors/selectImprovementsCost.ts';
import { selectReconciliation } from '../../../../redux/features/selectors/selectReconciliation.ts';
import { selectSiteCostPerSf } from '../../../../redux/features/selectors/selectSiteCostPerSf.ts';
import { selectSiteValues } from '../../../../redux/features/selectors/selectSiteValue.ts';
import {
  selectFeatureGridActiveConflictedFields,
  selectFeatureGridConflictedFields,
  selectTriggeredAlerts,
} from '../../../../redux/features/selectors/uiSelectors.ts';
import {
  clearFocusedCostApproachPropertyId,
  clearTriggeredAlert,
  unsuppressAlertToast,
} from '../../../../redux/features/uiSlice.ts';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks.ts';
import { usePropertyImageEntities } from '../../../../utils/usePropertyImages.tsx';
import { allCompsAdjustedDown } from '../../../alerts/definitions/adjustments/allCompsAdjustedDown.ts';
import { allCompsAdjustedUp } from '../../../alerts/definitions/adjustments/allCompsAdjustedUp.ts';
import { inconsistentCompAdjustment } from '../../../alerts/definitions/adjustments/inconsistentCompAdjustment.ts';
import { limitedAdjustedSalePriceRange } from '../../../alerts/definitions/adjustments/limitedAdjustedSalePriceRange.ts';
import { missingCompAdjustment } from '../../../alerts/definitions/adjustments/missingCompAdjustment.ts';
import { unexpectedCompAdjustment } from '../../../alerts/definitions/adjustments/unexpectedCompAdjustment.ts';
import { bracketedGLA } from '../../../alerts/definitions/comparable/bracketedGLA.ts';
import { bracketedSiteSize } from '../../../alerts/definitions/comparable/bracketedSiteSize.ts';
import { inconsistentSubjectBedBathCount } from '../../../alerts/definitions/comparable/inconsistentSubjectBedBathCount.ts';
import { inconsistentSubjectGLA } from '../../../alerts/definitions/comparable/inconsistentSubjectGLA.ts';
import { lessThanThreeSettledSales } from '../../../alerts/definitions/comparable/lessThanThreeSettledSales.ts';
import { missingPropertyData } from '../../../alerts/definitions/comparable/missingPropertyData.ts';
import { outsideProximityRequirement } from '../../../alerts/definitions/comparable/outsideProximityRequirement.ts';
import { propertyWithoutPhoto } from '../../../alerts/definitions/comparable/propertyWithoutPhoto.ts';
import { similarComparableToSubjectPriorSale } from '../../../alerts/definitions/comparable/similarComparableToSubjectPriorSale.ts';
import { effectiveAgeLowerThanActualAge } from '../../../alerts/definitions/depreciatedCost/effectiveAgeLowerThanActualAge.ts';
import { largestSiteHasHighestValue } from '../../../alerts/definitions/depreciatedCost/largestSiteHasHighestValue.ts';
import { negativeSiteValue } from '../../../alerts/definitions/depreciatedCost/negativeSiteValue.ts';
import { smallestSiteHasLowestValue } from '../../../alerts/definitions/depreciatedCost/smallestSiteHasLowestValue.ts';
import { missingOrderDetailsData } from '../../../alerts/definitions/orderDetails/missingOrderDetailsData.ts';
import { AlertDefinition, AlertDefinitionParams } from '../../../alerts/types.ts';

const DISMISSED_ALERT_ALIASES: Partial<Record<AlertType, AlertType>> = {
  inconsistentCompGlaAdjustment: 'inconsistentCompAdjustment',
  inconsistentCompSiteAdjustment: 'inconsistentCompAdjustment',
  conditionAdjustedUpAgainstSubject: 'inconsistentCompAdjustment',
  conditionAdjustedUpAgainstSubjectAiv: 'inconsistentCompAdjustmentAiv',
  conditionAdjustedUpAgainstSubjectArv: 'inconsistentCompAdjustmentArv',
};

const isAlertDismissed = (alertType: AlertType, dismissedAlerts: AlertType[]): boolean => {
  if (dismissedAlerts.includes(alertType)) return true;

  return dismissedAlerts.some(
    (dismissedType) => DISMISSED_ALERT_ALIASES[dismissedType] === alertType,
  );
};

const buildAlertSection = (
  alertDefinitions: AlertDefinition[],
  params: AlertDefinitionParams,
  dismissedAlerts: AlertType[],
  isInspectifyOrder: boolean,
): Alert[] =>
  alertDefinitions
    .map((alertDefinition) => alertDefinition(params))
    .filter(isDefined)
    .filter(
      (alert) =>
        (!isInspectifyOrder && alert.orderType.includes('saas')) ||
        (isInspectifyOrder && alert.orderType.includes('appraisify')),
    )
    .map((alert) => ({
      ...alert,
      status: isAlertDismissed(alert.type, dismissedAlerts) ? 'dismissed' : alert.status,
    }));

const splitCompSequenceByType = (
  compSequence: string[],
  compTypes: { [key: string]: CompType | undefined } | undefined,
) => ({
  aiv: compSequence.filter((id) => !compTypes?.[id] || compTypes?.[id] === 'aiv'),
  arv: compSequence.filter((id) => compTypes?.[id] === 'arv'),
});

const EMPTY_ALERTS: Record<AlertSection, Alert[]> = {
  adjustments: [],
  comparable: [],
  depreciatedCost: [],
};

const AlertsContext = createContext<Record<AlertSection, Alert[]> | null>(null);
const AlertsRefContext = createContext<MutableRefObject<Record<AlertSection, Alert[]>> | null>(
  null,
);

export const AlertsProvider = ({ children }: { children: ReactNode }) => {
  const alertsRef = useRef(EMPTY_ALERTS);
  const alerts = useComputeAlerts();
  alertsRef.current = alerts;

  return (
    <AlertsRefContext.Provider value={alertsRef}>
      <AlertsContext.Provider value={alerts}>{children}</AlertsContext.Provider>
    </AlertsRefContext.Provider>
  );
};

export const useAlerts = (): Record<AlertSection, Alert[]> => {
  const alerts = useContext(AlertsContext);
  if (!alerts) {
    throw new Error('useAlerts must be used within an AlertsProvider');
  }
  return alerts;
};

export const useAlertsRef = (): MutableRefObject<Record<AlertSection, Alert[]>> => {
  const alertsRef = useContext(AlertsRefContext);
  if (!alertsRef) {
    throw new Error('useAlertsRef must be used within an AlertsProvider');
  }
  return alertsRef;
};

const useComputeAlerts = (): Record<AlertSection, Alert[]> => {
  const dispatch = useAppDispatch();
  const { dismissedAlerts } = useAppSelector(selectAlerts);
  const compAdjustments = useAppSelector(selectCompAdjustments);
  const compSequence = useAppSelector(selectCompSequence);
  const compTypes = useAppSelector(selectCompTypes);
  const improvementEntityIds = useAppSelector(selectImprovementEntityIds);
  const improvementCosts = useAppSelector(selectImprovementsCosts);
  const order = useAppSelector(selectOrder);
  const priorSales = useAppSelector(selectPriorSales);
  const propertyDataEntities = useAppSelector(selectPropertyDataEntities);
  const siteValues = useAppSelector(selectSiteValues);
  const reconciliation = useAppSelector(selectReconciliation);
  const propertyImageEntities = usePropertyImageEntities();
  const activeConflictedFields = useAppSelector(selectFeatureGridActiveConflictedFields);
  const conflictedFields = useAppSelector(selectFeatureGridConflictedFields);

  const includeCostApproach = useAppSelector(selectIncludeCostApproach);
  const vacantLandSales = useVacantLandSales();
  const subjectEditableData = useAppSelector((state) =>
    selectEditablePropertyDataById(state, 'subject'),
  );
  const subjectCostPerSF = useAppSelector((state) =>
    selectSiteCostPerSf(state, 'subject'),
  )?.toDollarLike();
  const triggeredAlerts = useAppSelector(selectTriggeredAlerts);
  const areAlertsReady = ![
    dismissedAlerts,
    compAdjustments,
    compSequence,
    improvementCosts,
    order,
    priorSales,
    propertyDataEntities,
    siteValues,
    propertyImageEntities,
  ].some(isUndefined);

  const vacantLandValue = useMemo(() => {
    const subjectSiteArea = subjectEditableData?.site?.area;
    const vacantLandSalesChartData = getVacantLandChartData(vacantLandSales);
    if (
      !vacantLandSales ||
      !subjectSiteArea ||
      isUndefined(subjectCostPerSF) ||
      !vacantLandSalesChartData ||
      vacantLandSalesChartData.length < 2
    ) {
      return undefined;
    }

    const siteSize = schAloftArea.parse(subjectSiteArea).getUnitAmount('sqft');

    const { getCostPerSFFromSiteSize } = getVacantLandSalesExponentialFit(vacantLandSalesChartData);

    return AloftPrice.fromDollarLike(
      Math.min(getCostPerSFFromSiteSize(siteSize) * siteSize, Number.MAX_VALUE / 100),
    );
  }, [vacantLandSales, subjectEditableData?.site?.area, subjectCostPerSF]);

  if (
    [
      dismissedAlerts,
      compAdjustments,
      compSequence,
      improvementCosts,
      order,
      priorSales,
      propertyDataEntities,
      siteValues,
      propertyImageEntities,
    ].some(isUndefined)
  ) {
    return EMPTY_ALERTS;
  }

  const alertDefinitionParams: AlertDefinitionParams = useMemo(
    () => ({
      compAdjustments,
      compSequence,
      improvementCosts,
      order,
      priorSales,
      propertyDataEntities,
      siteValues,
      improvementEntityIds,
      reconciliation,
      propertyImageEntities,
      vacantLandValue,
      activeConflictedFields,
      conflictedFields,
      fullCompSequence: compSequence, // full sequence is needed for property name resolution in missingPropertyData alert
    }),
    [
      compAdjustments,
      compSequence,
      improvementCosts,
      order,
      priorSales,
      propertyDataEntities,
      siteValues,
      improvementEntityIds,
      reconciliation,
      propertyImageEntities,
      vacantLandValue,
      activeConflictedFields,
      conflictedFields,
    ],
  );

  const adjustments: Alert[] = useMemo(() => {
    const definitions = [
      limitedAdjustedSalePriceRange,
      allCompsAdjustedDown,
      allCompsAdjustedUp,
      inconsistentCompAdjustment,
      missingCompAdjustment,
      unexpectedCompAdjustment,
    ];

    if (!order.hasArvOption) {
      return buildAlertSection(
        definitions,
        alertDefinitionParams,
        dismissedAlerts,
        order.isInspectifyOrder,
      );
    }

    const { aiv: aivCompSequence, arv: arvCompSequence } = splitCompSequenceByType(
      compSequence,
      compTypes,
    );

    const aivAlerts = buildAlertSection(
      definitions,
      { ...alertDefinitionParams, compSequence: aivCompSequence, compType: 'aiv' },
      dismissedAlerts,
      order.isInspectifyOrder,
    );
    const arvAlerts = buildAlertSection(
      definitions,
      { ...alertDefinitionParams, compSequence: arvCompSequence, compType: 'arv' },
      dismissedAlerts,
      order.isInspectifyOrder,
    );

    return [...aivAlerts, ...arvAlerts];
  }, [alertDefinitionParams, dismissedAlerts, order.hasArvOption, compSequence, compTypes]);

  const comparable: Alert[] = useMemo(() => {
    const definitions = [
      missingOrderDetailsData,
      lessThanThreeSettledSales,
      bracketedGLA,
      bracketedSiteSize,
      similarComparableToSubjectPriorSale,
      propertyWithoutPhoto,
      missingPropertyData,
    ];

    const subjectDefinitions = [inconsistentSubjectBedBathCount, inconsistentSubjectGLA];

    if (!order.hasArvOption) {
      return buildAlertSection(
        [...definitions, ...subjectDefinitions],
        alertDefinitionParams,
        dismissedAlerts,
        order.isInspectifyOrder,
      );
    }

    const { aiv: aivCompSequence, arv: arvCompSequence } = splitCompSequenceByType(
      compSequence,
      compTypes,
    );

    const aivAlerts = buildAlertSection(
      definitions,
      {
        ...alertDefinitionParams,
        compSequence: aivCompSequence,
        compType: 'aiv',
        propertyType: 'comp',
      },
      dismissedAlerts,
      order.isInspectifyOrder,
    );
    const arvAlerts = buildAlertSection(
      definitions,
      {
        ...alertDefinitionParams,
        compSequence: arvCompSequence,
        compType: 'arv',
        propertyType: 'comp',
      },
      dismissedAlerts,
      order.isInspectifyOrder,
    );

    const proximityAlert = buildAlertSection(
      [outsideProximityRequirement],
      { ...alertDefinitionParams, compSequence },
      dismissedAlerts,
      order.isInspectifyOrder,
    );

    const subjectAlerts = buildAlertSection(
      subjectDefinitions,
      { ...alertDefinitionParams, propertyType: 'subject' },
      dismissedAlerts,
      order.isInspectifyOrder,
    );

    return [...subjectAlerts, ...aivAlerts, ...arvAlerts, ...proximityAlert];
  }, [
    alertDefinitionParams,
    dismissedAlerts,
    order.hasArvOption,
    compSequence,
    compTypes,
    propertyImageEntities,
  ]);

  const depreciatedCost: Alert[] = useMemo(
    () =>
      buildAlertSection(
        [
          ...(includeCostApproach
            ? [
                effectiveAgeLowerThanActualAge,
                negativeSiteValue,
                // siteValueIsLessThanVacantLandValue, // AV-940
                smallestSiteHasLowestValue,
                largestSiteHasHighestValue,
                // bracketedSiteToValueRatio, // AV-940
                // limitedSiteToValueRatioRange, // AV-940
              ]
            : []),
        ],
        alertDefinitionParams,
        dismissedAlerts,
        order.isInspectifyOrder,
      ),
    [alertDefinitionParams, dismissedAlerts, includeCostApproach],
  );

  useEffect(() => {
    if (!areAlertsReady) {
      return;
    }

    const activeAlertTypes = new Set(
      [...comparable, ...depreciatedCost, ...adjustments].map((alert) => alert.type),
    );
    const staleTypes = triggeredAlerts.filter((type) => !activeAlertTypes.has(type));
    staleTypes.forEach((type) => {
      dispatch(clearTriggeredAlert(type));
      dispatch(unsuppressAlertToast(type));
      if (type === 'negativeSiteValue') {
        dispatch(clearFocusedCostApproachPropertyId());
      }
    });
  }, [adjustments, areAlertsReady, comparable, depreciatedCost, dispatch, triggeredAlerts]);

  return useMemo(
    () => ({ comparable, depreciatedCost, adjustments }),
    [adjustments, comparable, depreciatedCost],
  );
};
